export enum VehicleStatus {
  AVAILABLE = 'available',
  RENTED = 'rented',
  MAINTENANCE = 'maintenance',
  OUT_OF_SERVICE = 'out-of-service',
  RESERVED = 'reserved'
}

export enum MaintenanceType {
  ROUTINE = 'routine',
  REPAIR = 'repair',
  INSPECTION = 'inspection',
  CLEANING = 'cleaning',
  EMERGENCY = 'emergency'
}

export interface MaintenanceRecord {
  type: MaintenanceType;
  description: string;
  scheduledDate: Date;
  completedDate?: Date;
  cost?: number;
  mileage?: number;
  notes?: string;
  performedBy?: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

export interface VehicleIssue {
  reportedDate: Date;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  reportedBy?: string;
  assignedTo?: string;
  resolvedDate?: Date;
  resolution?: string;
}

export interface VehicleImage {
  url: string;
  description?: string;
  uploadedDate: Date;
  type: 'exterior' | 'interior' | 'damage' | 'document';
}

export interface VehicleDocument {
  name: string;
  url: string;
  uploadedDate: Date;
  type: 'registration' | 'insurance' | 'inspection' | 'manual' | 'other';
}

export interface RateTier {
  title: string;
  rate: number;
  minDays: number;
  maxDays: number | null;
}

export interface Vehicle {
  _id: string;
  name: string;
  brand: string;
  model: string;
  year: number;
  kmh: number;
  description?: string;
  isAutomatic: boolean;
  defaultRatePerDay: number;
  defaultUnlimitedRatePerDay: number;
  rates: RateTier[];
  unlimedRates: RateTier[];
  seats: number;
  bags: number;
  totalQuantity: number;
  
  // Fleet Management Fields
  status: VehicleStatus;
  currentMileage: number;
  licensePlate?: string;
  vin?: string;
  color?: string;
  fuelType?: string;
  location?: string;
  lastServiceDate?: Date;
  nextServiceDue?: Date;
  serviceIntervalMiles: number;
  serviceIntervalDays: number;
  maintenanceHistory: MaintenanceRecord[];
  issues: VehicleIssue[];
  images: VehicleImage[];
  documents: VehicleDocument[];
  isActive: boolean;
  
  // Financial and Performance Data
  purchaseDate?: Date;
  purchasePrice?: number;
  currentValue?: number;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiryDate?: Date;
  registrationNumber?: string;
  registrationExpiryDate?: Date;
  
  // Booking Statistics
  totalBookings?: number;
  totalRevenue?: number;
  activeBookings?: number;
  averageRating?: number;
  totalReviews?: number;
}

export interface VehicleFilters {
  status?: VehicleStatus;
  location?: string;
  brand?: string;
  model?: string;
  isActive?: boolean;
  maintenanceDue?: boolean;
}

export interface CreateVehicleDto {
  name: string;
  brand: string;
  model: string;
  year: number;
  kmh: number;
  description?: string;
  isAutomatic: boolean;
  defaultRatePerDay: number;
  defaultUnlimitedRatePerDay: number;
  rates?: RateTier[];
  unlimedRates?: RateTier[];
  seats: number;
  bags: number;
  totalQuantity: number;
  licensePlate?: string;
  vin?: string;
  color?: string;
  fuelType?: string;
  location?: string;
  serviceIntervalMiles?: number;
  serviceIntervalDays?: number;
  purchaseDate?: Date;
  purchasePrice?: number;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceExpiryDate?: Date;
  registrationNumber?: string;
  registrationExpiryDate?: Date;
}

export interface UpdateVehicleStatusDto {
  status: VehicleStatus;
  notes?: string;
}

export interface ScheduleMaintenanceDto {
  type: MaintenanceType;
  description: string;
  scheduledDate: Date;
  estimatedCost?: number;
  notes?: string;
}

export interface FleetSummary {
  totalVehicles: number;
  availableVehicles: number;
  rentedVehicles: number;
  maintenanceVehicles: number;
  outOfServiceVehicles: number;
  utilizationRate: number;
  maintenanceDueCount: number;
}

export interface UpcomingReturn {
  returnDate: Date;
  bookingReference: string;
  customerName: string;
}

export interface VehicleAvailability {
  carId: string;
  carName: string;
  brand: string;
  model: string;
  totalQuantity: number;
  activeBookings: number;
  availableNow: number;
  isFullyBooked: boolean;
  nextAvailableDate: Date | null;
  nextAvailableBookingRef: string | null;
  upcomingReturns: UpcomingReturn[];
}